import React from 'react';
import { Drawer } from '../../../../../../../ui/components/Common/Drawer/Drawer';
import { CCAConfig } from '../../../../../../../utils/types/cca_types';
import {
  CCA_LEVEL_LABELS,
  CCA_SUBDOMAINS,
} from '../../../../../../questions/CCABank/ccaConstants';
import { useQuickBuild } from '../hooks/useQuickBuild';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  jobOpeningId: string;
  existingConfig?: CCAConfig;
}

export const QuickBuildDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  jobOpeningId,
  existingConfig,
}) => {
  const {
    config,
    setConfig,
    isGenerating,
    mcCount,
    imageCount,
    handleGenerate,
    toggleSubdomain,
  } = useQuickBuild({ jobOpeningId, existingConfig });

  const inputClass =
    'rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300 w-full';

  const footer = (
    <>
      <button
          type='button'
          disabled={isGenerating || config.subdomains.length === 0}
          onClick={handleGenerate}
          className='w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
      >
        { isGenerating ? (
          <span className='flex items-center justify-center gap-2'>
            <span className='h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent' />
            Generating…
          </span>
        ) : (
          'Generate Questions'
        ) }
      </button>
      { config.subdomains.length === 0 && (
        <p className='mt-2 text-center text-xs text-slate-400'>
          Select at least one subdomain
        </p>
      ) }
    </>
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title='Quick Build' footerContent={footer}>
      <div className='space-y-5'>

        { /* Subdomains */ }
        <div>
          <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500'>
            Subdomains
          </p>
          <div className='flex flex-col gap-1.5'>
            { CCA_SUBDOMAINS.map((sd) => (
              <button
                  key={sd}
                  type='button'
                  onClick={() => toggleSubdomain(sd)}
                  className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                    config.subdomains.includes(sd)
                      ? 'bg-emerald-50 text-slate-800 ring-1 ring-emerald-200'
                      : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                  }`}
              >
                <span
                    className={`h-2 w-2 shrink-0 rounded-full transition-colors ${
                      config.subdomains.includes(sd) ? 'bg-emerald-500' : 'bg-slate-300'
                    }`}
                />
                { sd }
              </button>
            )) }
          </div>
        </div>

        { /* Cognitive Level */ }
        <div>
          <div className='mb-2 flex items-center justify-between'>
            <label className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
              Cognitive Level (1–10)
            </label>
            <span className='rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700'>
              { config.level } — { CCA_LEVEL_LABELS[config.level] }
            </span>
          </div>
          <input
              type='range'
              min={1}
              max={10}
              value={config.level}
              onChange={(e) =>
                setConfig((p) => ({ ...p, level: Number(e.target.value) }))
              }
              className='w-full accent-emerald-500'
          />
        </div>

        { /* Time Limit */ }
        <div>
          <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
            Time Limit Per Question (seconds)
          </label>
          <input
              type='number'
              min={10}
              max={300}
              value={config.timeLimitSeconds}
              onChange={(e) =>
                setConfig((p) => ({ ...p, timeLimitSeconds: Number(e.target.value) }))
              }
              className={inputClass}
          />
        </div>

        { /* Total Questions */ }
        <div>
          <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
            Total Questions
          </label>
          <input
              type='number'
              min={1}
              max={20}
              value={config.totalQuestions}
              onChange={(e) =>
                setConfig((p) => ({
                  ...p,
                  totalQuestions: Number(e.target.value),
                  shortAnswerCount: Math.min(p.shortAnswerCount, Number(e.target.value)),
                  wordyCount: Math.min(p.wordyCount, Number(e.target.value)),
                }))
              }
              className={inputClass}
          />
        </div>

        { /* SA / MC Split */ }
        <div>
          <div className='mb-1.5 flex items-center justify-between'>
            <label className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
              Short Answer / MC Split
            </label>
            <span className='text-xs'>
              <span className='font-semibold text-violet-600'>{ config.shortAnswerCount } SA</span>
              <span className='text-slate-400'> / </span>
              <span className='font-semibold text-indigo-600'>{ mcCount } MC</span>
            </span>
          </div>
          <input
              type='range'
              min={0}
              max={config.totalQuestions}
              value={config.shortAnswerCount}
              onChange={(e) =>
                setConfig((p) => ({ ...p, shortAnswerCount: Number(e.target.value) }))
              }
              className='w-full accent-violet-500'
          />
        </div>

        { /* Wordy / Image Split */ }
        <div>
          <div className='mb-1.5 flex items-center justify-between'>
            <label className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
              Wordy / Image Split
            </label>
            <span className='text-xs'>
              <span className='font-semibold text-amber-600'>{ config.wordyCount } Wordy</span>
              <span className='text-slate-400'> / </span>
              <span className='font-semibold text-orange-600'>{ imageCount } Image</span>
            </span>
          </div>
          <input
              type='range'
              min={0}
              max={config.totalQuestions}
              value={config.wordyCount}
              onChange={(e) =>
                setConfig((p) => ({ ...p, wordyCount: Number(e.target.value) }))
              }
              className='w-full accent-amber-500'
          />
        </div>

      </div>
    </Drawer>
  );
};
